import { apiClient } from '@/api/client';
import { User } from '@/api/models';
import { useEntityDrawer } from '@/hooks/useEntityDrawer';
import { queryKeys } from '@/lib/queryKeys';
import { UserFormData } from '@/pages/users/hooks/useUserMutations';
import {
  PlanFormData,
  useUserPlanMutations,
} from '@/pages/users/hooks/useUserPlanMutations';
import { useUserPlans } from '@/pages/users/hooks/useUserPlans';
import * as React from 'react';
import { UserDrawerView } from './UserDrawerView';

export interface UserDrawerProps {
  onFormSubmit: (formData: UserFormData) => Promise<void>;
  onDeleteUser: (user: User) => void;
  isFormLoading: boolean;
}

export const UserDrawer: React.FC<UserDrawerProps> = ({
  onFormSubmit,
  onDeleteUser,
  isFormLoading,
}) => {
  const { state, actions, isLoading } = useEntityDrawer<User>({
    basePath: '/users',
    queryKey: queryKeys.ADMIN_USER,
    fetchFn: async (id: number) => {
      const response = await apiClient.GET('/admin/users/{id}', {
        params: { path: { id } },
      });
      if (response.error) throw new Error('Failed to fetch user');
      return response.data as User;
    },
    supportsCreate: true,
  });

  const { state: plansState, actions: plansActions } = useUserPlans(
    state.entity?.id,
  );
  const {
    actions: planMutationActions,
    isDeleting: isPlanDeleting,
    isCreating: isPlanCreating,
    isUpdating: isPlanUpdating,
  } = useUserPlanMutations();

  const handlePlanFormSubmit = async (data: PlanFormData) => {
    if (!state.entity?.id) return;
    await planMutationActions.handleCreatePlan(state.entity.id, data);
  };

  return (
    <UserDrawerView
      isOpen={state.isOpen}
      user={state.entity}
      mode={state.mode}
      isLoading={isLoading}
      isFormLoading={isFormLoading}
      plansState={plansState}
      plansActions={plansActions}
      planMutationActions={planMutationActions}
      isPlanDeleting={isPlanDeleting}
      isPlanFormLoading={isPlanCreating || isPlanUpdating}
      onClose={actions.close}
      onEdit={user => actions.edit(user.id!)}
      onDelete={onDeleteUser}
      onFormSubmit={onFormSubmit}
      onPlanFormSubmit={handlePlanFormSubmit}
    />
  );
};
